import React, { useState } from 'react';
import { Divider, IconButton, Menu, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import { listCardActionIconSx } from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { recordCardHeaderIconSx } from '../../Map/components/RecordLotIdentity';
import { metaWriteMenuModel } from '../metaWrite';

/*
 * ⋮ de Campañas (campaña, anuncio y, desde F2b, conjunto en Presupuesto): el mismo botón y menú que
 * «Más acciones» de Historial. Sale de `MetaCampaignsView` para que el detalle de la campaña lo use
 * sin importar la vista entera.
 */

// Menú de vínculo con el mismo botón y menú que «Más acciones» de las cards de Historial
// (`HistoryPanel`): ⋮ sin borde (`listCardActionIconSx` + `recordCardHeaderIconSx`) y
// `ActionMenuItem` con ícono a la izquierda (`ACTION_MENU_PAPER_PROPS`). El ancho se
// adapta a los textos largos («Desvincular este anuncio de todas sus unidades»).
const linkMenuPaperProps = {
  ...ACTION_MENU_PAPER_PROPS,
  sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 'auto', minWidth: '164px', maxWidth: 'min(340px, 92vw)' },
};
const linkMenuIconSx = { fontSize: 20 };
const LINK_MENU_ICONS = {
  'link-ad': <AddLinkIcon sx={linkMenuIconSx} />,
  'unlink-ad': <LinkOffIcon sx={linkMenuIconSx} />,
  'link-campaign': <LinkIcon sx={linkMenuIconSx} />,
  'unlink-campaign': <LinkOffIcon sx={linkMenuIconSx} />,
  // F2a + F2b: acciones en Meta (primer grupo del ⋮; Eliminar, aparte y en rojo).
  'meta-pause': <PauseCircleOutlineIcon sx={linkMenuIconSx} />,
  'meta-resume': <PlayCircleOutlineIcon sx={linkMenuIconSx} />,
  'meta-rename': <DriveFileRenameOutlineIcon sx={linkMenuIconSx} />,
  'meta-set_daily_budget': <PaymentsOutlinedIcon sx={linkMenuIconSx} />,
  'meta-set_lifetime_budget': <PaymentsOutlinedIcon sx={linkMenuIconSx} />,
  'meta-budget': <PaymentsOutlinedIcon sx={linkMenuIconSx} />,
  'meta-set_end_time': <EventOutlinedIcon sx={linkMenuIconSx} />,
  'meta-archive': <ArchiveOutlinedIcon sx={linkMenuIconSx} />,
  'meta-delete': <DeleteOutlineIcon sx={linkMenuIconSx} />,
};

/**
 * Menú secundario (⋮; por defecto «Más opciones de vínculo», `label` lo cambia; `icons` = íconos por `key`). `items`:
 * `{ key, label, onClick, danger?, group?, muted?, tooltip? }`. Orden: acciones de Meta que editan
 * (`group: 'meta'`), después el vínculo que suma, y al final las que sacan (Archivar, Eliminar y
 * Desvincular), cada grupo separado por un divider. `muted`: en gris legible con su `tooltip`
 * (el clic sigue llegando a `onClick`, que puede no hacer nada o abrir ⚙).
 */
export function LinkMenu({ items, disabled = false, testId, label = 'Más opciones de vínculo', icons = null, buttonSx = null }) {
  const [anchor, setAnchor] = useState(null);
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  // Archivar entra al grupo de abajo solo cuando se puede usar. En gris se queda con el resto.
  const removes = (it) => it.danger || (it.key === 'meta-archive' && !it.muted);
  const meta = list.filter((it) => it.group === 'meta' && !removes(it));
  const normal = list.filter((it) => it.group !== 'meta' && !it.danger);
  const danger = list.filter(removes);
  const renderItem = (it) => {
    const item = (
      <ActionMenuItem
        key={it.key}
        icon={(icons || LINK_MENU_ICONS)[it.key] || <LinkIcon sx={linkMenuIconSx} />}
        label={it.label}
        danger={Boolean(it.danger)}
        muted={Boolean(it.muted)}
        testId={`${testId}-${it.key}`}
        onClick={() => {
          if (it.muted && !it.closeOnClick) return;
          setAnchor(null);
          it.onClick?.();
        }}
      />
    );
    if (!it.tooltip) return item;
    // ActionMenuItem no reenvía la ref: el Tooltip va sobre un span de bloque (como en Historial).
    return (
      <Tooltip key={it.key} title={<span>{it.tooltip}</span>} placement="left" arrow>
        <span style={{ display: 'block' }} data-testid={`${testId}-${it.key}-tip`}>{item}</span>
      </Tooltip>
    );
  };
  const groups = [meta, normal, danger].filter((g) => g.length > 0);
  return (
    <>
      <Tooltip title={<span>{label}</span>} placement="top" arrow describeChild>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={anchor ? 'true' : undefined}
            disabled={disabled}
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx, ...buttonSx }}
            data-testid={testId}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={linkMenuPaperProps}
        MenuListProps={ACTION_MENU_LIST_PROPS}
      >
        {groups.flatMap((g, i) => [
          i > 0 ? <Divider key={`div-${i}`} sx={{ borderColor: '#4A4A4A', my: '0 !important' }} /> : null,
          ...g.map(renderItem),
        ]).filter(Boolean)}
      </Menu>
    </>
  );
}

/**
 * F2a + F2b: ítems de Meta del ⋮ (campaña, conjunto o anuncio) a partir del bloque `write`
 * (`metaWriteMenuModel`). Sin `writeActions` (sin fase 1 / sin el bloque) → `[]`: el ⋮ queda como antes.
 * `extra`: `{ budgetType, amount, currency, endsAt, campaignId }` (`metaWriteBudgetOf`) para presupuesto y fecha.
 */
export function metaWriteItems(writeActions, level, target, active, extra = {}) {
  if (!writeActions || !target) return [];
  const { budgetType = null, amount = null, currency = null, endsAt = null, campaignId = null } = extra;
  return metaWriteMenuModel({ write: target.write, level, active, isOwner: writeActions.isOwner, budgetType }).map((m) => ({
    key: `meta-${m.action}`,
    group: 'meta',
    label: m.label,
    muted: m.muted,
    tooltip: m.tooltip,
    danger: m.danger,
    // Dueño sin la cuenta habilitada / sin permiso: el clic abre ⚙ (al editor no le llega nada).
    closeOnClick: m.opensSettings,
    onClick: m.muted
      ? (m.opensSettings ? writeActions.onOpenSettings : null)
      : () => writeActions.onAction({
        level,
        action: m.action,
        id: target.id,
        name: target.name,
        campaignId: campaignId || target.campaignId || target.id,
        currency: currency || target.currency || null,
        currentAmount: amount,
        endsAt,
      }),
  }));
}
